import { ReplaySubject } from 'rx';
import { EventResultsStreams } from '../data/EventResultsStreams';
import { stripLeadingZero } from '../shared/util';

/**
 * @property {string} bib
 * @property {string} displayName
 * @property {number} eventId
 * @property {Event} event
 * @property {string} eventName
 * @property {string} courseName
 * @property {number} eventCourseId
 * @property {[]} legs
 * @property {[]} brackets
 * @property {boolean} isCtLive
 * @property {string} ticksString
 * @property {boolean} ivIsChipTime
 * @property {string} ivGunTimeWithPenalty
 * @property {string} ivTimeWithPenalty
 * @property {string} entryStatus
 */
export class Result {
  constructor(data = {}) {
    this.legs = [];
    this.brackets = [];
    Object.assign(this, data);
  }

  static get = (event, eventCourseId, bib) => {
    const resultSubject  = new ReplaySubject(1);
    EventResultsStreams.fetchEntryResultByEventId({
      eventId: event.athlinksId,
      eventCourseId,
      bib
    });

    EventResultsStreams.getEntryResult({eventCourseId, bib}).subscribe((res) => {
      if(!res.isLoading) {
        resultSubject.onNext(new Result({...res, event}));
      }
    });

    return resultSubject;
  };

  isLoaded = () => !!this.bib;

  getRanks = (t) => this.isCTLiveResults() ?
    this.getCtLiveRanks() : this.getAthlinksRanks(t);

  /**
   * Check if result came from ctlive. Checking isCTLive is not enough
   * If there is genID then result came from ctlive
   * @return {boolean}
   */
  isCTLiveResults = () => !!this.genId;

  getAthlinksRanks = (t) => {
    const [ leg ] = this.legs;
    if (!leg) return {};
    const {
      rankO, rankOTotal,
      rankG, rankGTotal,
      rankA, rankATotal,
    } = leg;
    const genderLabel = this.gender === 'M' ? t('Male') : t('Female');
    return {
      overall: { rank: rankO, total: rankOTotal, label: t('Overall') },
      gender: { rank:  rankG, total: rankGTotal, label: genderLabel },
      age: { rank: rankA, total: rankATotal, label: t('Age') },
    }
  };

  getCtLiveRanks = () => {
    const o = this.getBracketByType('OPEN');
    const g = this.getBracketByType('SEX');
    const a = this.getBracketByType('AGE');
    return {
      overall: { rank: o.rank, total: o.totalAthletes, label: o.bracketName },
      gender: { rank: g.rank, total: g.totalAthletes, label: g.bracketName },
      age: { rank: a.rank, total: a.totalAthletes, label: a.bracketName },
    };
  };

  getBracketByType = (type) => {
    if (!this.isCTLiveResults()) return {};
    if (type === 'AGE' && !!this.primaryBracketId) {
      return this.brackets.find(({id}) => id === this.primaryBracketId) || {};
    }
    return this.brackets.find(({bracketType}) => bracketType === type) || {};
  };

  getPace = () => {
    if (this.isCTLiveResults() && this.pace) {
      const [pace] = this.pace.split(' ');
      return stripLeadingZero(pace);
    }
    else {
      const [ { paceString = '--' } = {} ] = this.legs;
      return paceString;
    }
  };

  getPaceUnit = () => {
    if (this.isCTLiveResults() && this.pace) return this.pace.split(' ')[1];
    else {
      const [{paceUnit = '--'} = {}] = this.legs;
      return paceUnit;
    }
  };

  getFinishTime = (useGunTime = false) => {
    if(useGunTime) return stripLeadingZero(this.ivGunTimeWithPenalty);
    //It may be linked but with athlinks data, which may not have the 'iv' times
    // const hasCtLiveTimes = !!this.ivTimeWithPenalty || !!this.ivGunTimeWithPenalty;
    const finishTime = this.isCTLiveResults()
      ? this.ivIsChipTime
        ? this.ivTimeWithPenalty
        : this.ivGunTimeWithPenalty
      : this.ticksString;
    return stripLeadingZero(finishTime);
  };

  /**
   * @TODO Chip Start time requires IndividualResultsStreams.receiveIndividualCtLiveResultByEventAndEntry
   * @param {MasterEvent} masterEvent
   * @return {*[]}
   */
  getKioskInfo = (masterEvent, t) => {
    const data = [];
    if (!this.isLoaded()) {
      return data;
    }
    data.push({
      label: t('Date'),
      value: this.event.startEpochMs,
      type: 'date'
    });
    data.push({
      label: t('Distance'),
      value: masterEvent.getCourseDistance(this.eventId, this.eventCourseId),
      type: 'distance',
    });
    const gunTime = this.ivGunTimeWithPenalty;
    if (gunTime) {
      data.push({
        label: t('Gun time'),
        value: gunTime,
      });
    }
    data.push({
      label: t('Location'),
      type: 'location',
      value: {
        city: this.event.city,
        region: this.event.extractRegionId(),
      },
    });
    const temperature = this.event.getMaxTemperature();
    if (temperature) {
      data.push({
        label: t('Weather'),
        value: temperature,
        type: 'temperature'
      })
    }
    data.push({
      label: t('Bib Number'),
      value: this.bib
    });
    return data;
  }

  isDNF = () => this.entryStatus === 'DNF';

  isDQ = () => this.entryStatus === 'DQ';

  isFinisher = () => !this.isDNF() && !this.isDQ();

}



/** WEBPACK FOOTER **
 ** ./classes/Result.js
 **/